import { aiAgentClient } from "../api/ai-agent-client";

/**
 * OpenAI Whisper Speech-to-Text Adapter
 *
 * Provides speech recognition capabilities using OpenAI's Whisper API via backend
 * Records audio from the user's microphone and transcribes it to text
 */

export type RecordingState = "idle" | "recording" | "processing" | "error";

export interface RecordingStatus {
  state: RecordingState;
  duration: number;
  error?: Error;
}

export interface OpenAIWhisperSTTOptions {
  salonId: string;
  language?: string; // Optional language code (e.g., 'en', 'es', 'fr'). If not specified, Whisper will auto-detect.
}

export class OpenAIWhisperSTTAdapter {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private salonId: string;
  private language?: string;
  private startTime: number = 0;
  private durationInterval: NodeJS.Timeout | null = null;

  // Status tracking
  private currentStatus: RecordingStatus = {
    state: "idle",
    duration: 0,
  };
  private listeners = new Set<() => void>();

  constructor(options: OpenAIWhisperSTTOptions) {
    this.salonId = options.salonId;
    this.language = options?.language;
  }

  /**
   * Get current recording status
   */
  get status(): RecordingStatus {
    return this.currentStatus;
  }

  /**
   * Subscribe to status changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      callback();
    });
  }

  /**
   * Update recording status
   */
  private updateStatus(newStatus: Partial<RecordingStatus>): void {
    this.currentStatus = { ...this.currentStatus, ...newStatus };
    this.notifyListeners();
  }

  /**
   * Start recording audio from microphone
   */
  async startRecording(): Promise<void> {
    if (this.currentStatus.state === "recording") {
      console.warn("[OpenAIWhisperSTTAdapter] Already recording");
      return;
    }

    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Reset audio chunks
      this.audioChunks = [];

      // Create MediaRecorder
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
      });

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("[OpenAIWhisperSTTAdapter] MediaRecorder error:", event);
        this.updateStatus({
          state: "error",
          error: new Error("Recording failed"),
        });
        this.cleanup();
      };

      // Start recording
      this.mediaRecorder.start(100); // Collect data every 100ms
      this.startTime = Date.now();

      // Start duration tracking
      this.durationInterval = setInterval(() => {
        if (this.currentStatus.state === "recording") {
          const duration = Math.floor((Date.now() - this.startTime) / 1000);
          this.updateStatus({ duration });
        }
      }, 100);

      this.updateStatus({
        state: "recording",
        duration: 0,
        error: undefined,
      });
    } catch (error) {
      console.error("[OpenAIWhisperSTTAdapter] Failed to start recording:", error);

      let errorMessage = "Failed to access microphone";
      if (error instanceof Error) {
        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
          errorMessage = "Microphone permission denied";
        } else if (error.name === "NotFoundError") {
          errorMessage = "No microphone found";
        }
      }

      this.updateStatus({
        state: "error",
        error: new Error(errorMessage),
      });

      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop recording and transcribe audio
   */
  async stopRecording(): Promise<string> {
    if (this.currentStatus.state !== "recording") {
      throw new Error("Not currently recording");
    }

    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error("MediaRecorder not initialized"));
        return;
      }

      // Set up completion handler
      this.mediaRecorder.onstop = async () => {
        try {
          this.updateStatus({ state: "processing" });

          // Create audio blob
          const mimeType = this.mediaRecorder?.mimeType || "audio/webm";
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });

          // Transcribe audio
          const transcript = await this.transcribe(audioBlob);

          this.updateStatus({ state: "idle", duration: 0 });
          this.cleanup();

          resolve(transcript);
        } catch (error) {
          console.error("[OpenAIWhisperSTTAdapter] Transcription failed:", error);

          this.updateStatus({
            state: "error",
            error: error instanceof Error ? error : new Error("Transcription failed"),
          });

          this.cleanup();
          reject(error);
        }
      };

      // Stop recording
      this.mediaRecorder.stop();

      // Stop duration tracking
      if (this.durationInterval) {
        clearInterval(this.durationInterval);
        this.durationInterval = null;
      }
    });
  }

  /**
   * Cancel recording without transcription
   */
  cancelRecording(): void {
    if (this.mediaRecorder && this.currentStatus.state === "recording") {
      this.mediaRecorder.stop();
    }

    this.updateStatus({ state: "idle", duration: 0, error: undefined });
    this.cleanup();
  }

  /**
   * Transcribe audio blob using backend Whisper API
   */
  private async transcribe(audioBlob: Blob): Promise<string> {
    try {
      // Call backend transcription endpoint
      const transcript = await aiAgentClient.transcribeAudio(
        this.salonId,
        audioBlob,
        this.language
      );

      return transcript;
    } catch (error) {
      console.error("[OpenAIWhisperSTTAdapter] Transcription error:", error);
      throw error;
    }
  }

  /**
   * Get supported MIME type for MediaRecorder
   */
  private getSupportedMimeType(): string {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/mp4",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return ""; // Let browser use default
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.stream = null;
    }

    // Clear media recorder
    this.mediaRecorder = null;

    // Clear audio chunks
    this.audioChunks = [];

    // Clear duration interval
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }
}
